import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import z from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { DangerButton } from "components/atoms/DangerButton";
import ServiceMessageHeader from "components/atoms/ServiceMessageHeader";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import MultiSelectField from "components/organisms/form/MultiSelectField";
import SelectField from "components/organisms/form/SelectField";
import TextField from "components/organisms/form/TextField";
import TextareaField from "components/organisms/form/TextareaField";
import ToggleField from "components/organisms/form/ToggleField";
import { NonZodContentSegmentsEditor } from "pages/Content/FeedCards/CreateContentPage";
import {
	SingleServiceMessageQuery,
	useDeleteServiceMessageMutation,
	useSingleServiceMessageQuery,
	useUpsertServiceMessageMutation,
} from "pages/Content/ServiceMessages/ServiceMessages.graphql-gen";
import { dateFormat } from "utils/Utils";

import { ServiceMessageApplication, ServiceMessageLocation } from "generated/graphql-types";

function applicationLabels(application: ServiceMessageApplication) {
	if (application === ServiceMessageApplication.STROMBESTILLING) {
		return "Strømbestilling";
	}
	if (application === ServiceMessageApplication.BEDRIFTSPORTALEN) {
		return "Bedriftsportalen";
	}
	return "Mitt NTE";
}

function isMittNTELocation(location: ServiceMessageLocation) {
	return location !== ServiceMessageLocation.STROMBESTILLING_FRONTEND && location !== ServiceMessageLocation.STROMTILBUD;
}

function getLocationOptions(application: ServiceMessageApplication) {
	if (application === ServiceMessageApplication.STROMBESTILLING) {
		return [
			{ label: "Strømbestilling-frontend", value: "STROMBESTILLING_FRONTEND" },
			{ label: "Strømtilbud", value: "STROMTILBUD" },
		];
	}
	if (application === ServiceMessageApplication.BEDRIFTSPORTALEN) {
		return [];
	}
	return Object.keys(ServiceMessageLocation)
		.filter((l) => isMittNTELocation(l as ServiceMessageLocation))
		.map((l) => {
			return { label: l, value: l };
		});
}

export const ServiceMessageSchema = z.object({
	title: z.string().min(1),
	content: z.string().min(1),
	published: z.boolean(),
	publishDate: z.string().min(1),
	unPublishDate: z.string().optional(),
});

export default function ServiceMessageFormPage() {
	const { serviceMessageId } = useParams<{ serviceMessageId: string }>();
	const isEditMode = !!serviceMessageId;
	const navigate = useNavigate();
	const [upsertServiceMessage, { loading, error }] = useUpsertServiceMessageMutation();
	const [previewState, setPreviewState] = useState<Partial<SingleServiceMessageQuery["singleServiceMessage"]>>({});
	const [application, _setApplication] = useState<ServiceMessageApplication>(ServiceMessageApplication.MITT_NTE);
	const [location, setLocation] = useState<ServiceMessageLocation[]>([]);
	const [segmentIn, setSegmentIn] = useState<string[]>([]);
	const [segmentNotIn, setSegmentNotIn] = useState<string[]>([]);

	const { data } = useSingleServiceMessageQuery({
		variables: {
			serviceMessageId: serviceMessageId || "",
		},
		skip: !isEditMode,
	});

	useEffect(() => {
		if (data?.singleServiceMessage) {
			setPreviewState(data.singleServiceMessage);
			if (data.singleServiceMessage.location) setLocation(data.singleServiceMessage.location);
			if (data.singleServiceMessage.application) _setApplication(data.singleServiceMessage.application);
			if (data.singleServiceMessage.segmentIn) setSegmentIn(data.singleServiceMessage.segmentIn);
			if (data.singleServiceMessage.segmentNotIn) setSegmentNotIn(data.singleServiceMessage.segmentNotIn);
		}
	}, [data?.singleServiceMessage]);

	function setApplication(app: ServiceMessageApplication) {
		setLocation([]);
		setSegmentIn([]);
		setSegmentNotIn([]);
		_setApplication(app);
	}

	const deleteServiceMessage = useDeleteServiceMessageMutation()[0];

	return (
		<div>
			<MainLayoutPageHeader title={isEditMode ? "Rediger driftsmelding" : "Ny driftsmelding"} />
			<Flex gap="16px">
				<Form
					key={data?.singleServiceMessage?.id}
					loading={loading}
					value={
						isEditMode
							? {
									...data?.singleServiceMessage,
									publishDate: data?.singleServiceMessage?.publishDate
										? format(new Date(data.singleServiceMessage.publishDate), dateFormat)
										: undefined,
									unPublishDate: data?.singleServiceMessage?.unPublishDate
										? format(new Date(data.singleServiceMessage.unPublishDate), dateFormat)
										: undefined,
								}
							: { publishDate: format(new Date(), dateFormat) }
					}
					onSubmit={async (data) => {
						const result = await upsertServiceMessage({
							variables: {
								serviceMessage: {
									...data,
									id: serviceMessageId,
									unPublishDate: data.unPublishDate || undefined,
									application,
									segmentIn,
									segmentNotIn,
									location,
								},
							},
						});
						const id = result.data?.upsertServiceMessage?.id;
						!result.error && navigate(isEditMode ? `/service-messages` : `/service-messages/${id}`);
					}}
					schema={ServiceMessageSchema}
				>
					<Flex direction={Direction.column} gap="16px">
						<TextField
							name="title"
							label="Tittel"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const target = e.target;
								setPreviewState((oldState) => {
									return {
										...oldState,
										title: target.value || "",
									};
								});
							}}
						/>
						<TextareaField
							name="content"
							label="Innhold"
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
								const target = e.target;
								setPreviewState((oldState) => {
									return {
										...oldState,
										content: target.value || "",
									};
								});
							}}
						/>

						<TextField type="datetime-local" name="publishDate" label="Publiseringsdato" />
						<TextField type="datetime-local" name="unPublishDate" label="Avpubliseringsdato" />

						<SelectField
							label="Applikasjon"
							value={application}
							onChange={(v) => setApplication(v as ServiceMessageApplication)}
							options={Object.keys(ServiceMessageApplication).map((k) => {
								return { label: applicationLabels(k as ServiceMessageApplication), value: k };
							})}
						/>

						<MultiSelectField
							style={{ height: "150px" }}
							label="Plassering i appen"
							options={getLocationOptions(application)}
							onChange={(v) => setLocation(v as ServiceMessageLocation[])}
							value={location}
						/>

						{application === ServiceMessageApplication.MITT_NTE && (
							<NonZodContentSegmentsEditor
								segmentIn={segmentIn}
								segmentNotIn={segmentNotIn}
								setSegmentIn={setSegmentIn}
								setSegmentNotIn={setSegmentNotIn}
							/>
						)}

						{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
						<ToggleField name="published" label="Publisert?" />
						<FormButton label={isEditMode ? "Oppdater driftsmelding" : "Lag ny driftsmelding"} />

						{isEditMode && (
							<DangerButton
								label="Slett innhold"
								onClick={async () => {
									if (!window.confirm(`Sikker på at du ønsker å slette driftsmeldingen?`)) {
										return;
									}

									const { error } = await deleteServiceMessage({
										variables: { serviceMessageId: serviceMessageId || "" },
									});

									if (error) {
										return console.error(error);
									}

									navigate("/service-messages");
								}}
							/>
						)}
					</Flex>
				</Form>
				<Flex direction={Direction.column} gap="16px" style={{ width: "390px" }}>
					<ServiceMessageHeader serviceMessage={previewState} />
					<div
						style={{
							display: "inline-block",
							width: "390px",
						}}
					/>
				</Flex>
			</Flex>
		</div>
	);
}
